<div class="profile-form-wrapper">
  <!-- Show error message if there was an error loading the profile -->
  @if(error){
  <div class="error-message">
    <p>{{ error }}</p>
    <button (click)="loadUserProfile()">{{ 'common.retry' | transloco }}</button>
  </div>
  } @if(isLoading){
  <!-- Show skeleton loader when loading -->
  <app-skeleton-loader *ngIf="isLoading" [count]="3" type="list"></app-skeleton-loader>
  }

  <!-- Alert card for missing profile fields -->
  <app-alert-card *ngIf="!error && !isLoading && missingProfileFields.length > 0 && !isEditMode && !submissionError" class="missing-fields-alert">
    <div class="alert-content">
      <div class="header">
        <h4>{{ 'profile.missingFields.title' | transloco }}</h4>
        <p>{{ 'profile.missingFields.description' | transloco }}</p>
      </div>
      <ul class="missing-fields-list">
        <li *ngFor="let field of missingProfileFields">
          {{ field | transloco }}
        </li>
      </ul>
      <app-button variant="filled" color="primary" size="lg" (click)="toggleEditMode()">
        {{ 'profile.missingFields.completeProfile' | transloco }}
      </app-button>
    </div>
  </app-alert-card>

  <div class="profile-form" *ngIf="!error && !isLoading">
    <div class="profile-form-personal-data d-grid gap-md">
      <div class="section-header">
        <div class="section-header__text">
          <h2 class="section-heading">{{ 'profile.personal.title' | transloco }}</h2>
          <p class="section-description">{{ 'profile.personal.description' | transloco }}</p>
        </div>
        <app-button
          *ngIf="!isEditMode"
          variant="outline"
          color="secondary"
          [icon]="true"
          (click)="toggleEditMode()"
          [disabled]="isParsingInProgress"
          class="edit-button">
          <i class="fas fa-pencil-alt"></i>
          {{ 'profile.edit' | transloco }}
        </app-button>
      </div>
      <!-- Edit Mode -->
      <app-profile-personal-info-form
        *ngIf="isEditMode"
        #personalInfoFormComponent
        [personalInfoForm]="personalInfoForm"
        [error]="submissionError"
        [disabled]="isParsingInProgress"
        [userEmail]="fullUserProfile?.email || userEmail"
        [profileSettings]="profileSettings"
        [isAuthenticated]="isUserAuthenticated()"
        [missingFields]="missingProfileFields"
        [educationLevelOptions]="educationLevelOptions"
        [consentUpdatingTypes]="consentUpdatingTypes"
        [avatarPhoto]="avatarPhoto"
        [avatarFirstName]="avatarFirstName"
        [avatarLastName]="avatarLastName"
        [avatarIdentifier]="avatarIdentifier"
        [avatarLoading]="isAvatarLoading"
        (avatarEdit)="openAvatarDialog()"
        (avatarRemove)="removeAvatar()"
        (submitEvent)="submitForm()"
        (cancelEvent)="toggleEditMode()"
        (addressChange)="onAddressChange($event)"></app-profile-personal-info-form>

      <!-- Preview Mode -->
      <app-profile-personal-info-preview
        *ngIf="!isEditMode"
        [userProfile]="fullUserProfile"
        [isLoading]="isLoading"
        [isAuthenticated]="isUserAuthenticated()"
        [userEmail]="fullUserProfile?.email || userEmail"
        [educationLevelOptions]="educationLevelOptions"
        [avatarPhoto]="avatarPhoto"
        [avatarFirstName]="avatarFirstName"
        [avatarLastName]="avatarLastName"
        [avatarIdentifier]="avatarIdentifier"
        [avatarLoading]="isAvatarLoading"
        (avatarEdit)="openAvatarDialog()"
        (avatarRemove)="removeAvatar()"></app-profile-personal-info-preview>
    </div>

    <!-- CV Section - always visible when not in edit mode -->
    <div class="profile-form-cv d-grid gap-md" *ngIf="!isEditMode && missingProfileFields.length === 0">
      <div class="section-header__text">
        <h2 class="section-heading">{{ 'profile.cv.title' | transloco }}</h2>
        <p class="section-description">{{ 'profile.cv.description' | transloco }}</p>
      </div>

      <!-- Profile Fill Options - always visible in CV section -->
      <app-profile-fill-options
        [isAuthenticated]="isUserAuthenticated()"
        [showCvParsingLoader]="isParsingInProgress"
        [isParsingComplete]="isParsingComplete"
        [rateLimitInfo]="rateLimitInfo"
        [hasExistingProfileData]="hasExistingProfileData"
        [aiParsingConsentPreference]="effectiveAiParsingConsent"
        [aiParsingConsentUpdating]="consentUpdatingTypes.has(ConsentType.AI_PARSING)"
        (methodSelected)="onProfileFillMethodSelected($event)"
        (aiParsingConsentPreferenceChange)="onAiParsingConsentPreferenceChange($event)"
        (fileProcessed)="onFileProcessed($event)"></app-profile-fill-options>

      <!-- Individual sections - shown only when no missing fields and method is selected or user has existing data -->
      <div class="profile-sections" *ngIf="shouldShowProfileSections()">
        <!-- Work Experience Section -->
        <app-profile-section
          [title]="'profile.sections.workExperience' | transloco"
          [sectionType]="'workExperience'"
          [workExperiences]="fullUserProfile?.workExperiences || []"
          [isAuthenticated]="isUserAuthenticated()"
          (profileItemAction)="onProfileItemAction($event)"></app-profile-section>

        <!-- University Education Section -->
        <app-profile-section
          [title]="'profile.sections.university' | transloco"
          [sectionType]="'university'"
          [cvOptions]="cvOptions"
          [universities]="fullUserProfile?.universityEducations || []"
          [isAuthenticated]="isUserAuthenticated()"
          (profileItemAction)="onProfileItemAction($event)"></app-profile-section>

        <!-- High School Education Section -->
        <app-profile-section
          [title]="'profile.sections.highSchool' | transloco"
          [sectionType]="'highSchool'"
          [cvOptions]="cvOptions"
          [highSchools]="fullUserProfile?.highSchoolEducations || []"
          [isAuthenticated]="isUserAuthenticated()"
          (profileItemAction)="onProfileItemAction($event)"></app-profile-section>

        <!-- Languages Section -->
        <app-profile-section
          [title]="'profile.sections.language' | transloco"
          [sectionType]="'languages'"
          [cvOptions]="cvOptions"
          [languages]="fullUserProfile?.languages || []"
          [isAuthenticated]="isUserAuthenticated()"
          (profileItemAction)="onProfileItemAction($event)"></app-profile-section>

        <!-- Skills Section -->
        <app-profile-section
          [title]="'profile.sections.skills' | transloco"
          [sectionType]="'skills'"
          [cvOptions]="cvOptions"
          [skills]="fullUserProfile?.skills || []"
          [isAuthenticated]="isUserAuthenticated()"
          (profileItemAction)="onProfileItemAction($event)"></app-profile-section>

        <!-- Drivers License Section -->
        <app-drivers-license-section
          [driversLicenses]="fullUserProfile?.driversLicenses || null"
          [isAuthenticated]="isUserAuthenticated()"
          (driversLicenseChange)="onDriversLicenseChange($event)"></app-drivers-license-section>

        <!-- Projects Section -->
        <!-- <app-profile-section
        [title]="'profile.sections.projects' | transloco"
        [sectionType]="'projects'"
        [cvOptions]="cvOptions"
        [projects]="fullUserProfile?.projects || []"
        (profileItemAction)="onProfileItemAction($event)"></app-profile-section> -->
      </div>
    </div>
  </div>
</div>
